import React, { useState } from 'react';
import { TagIcon, XIcon, PlusIcon } from 'lucide-react';
import { SearchWithAutocomplete } from '../../../components/UI/SearchWithAutocomplete';
import { useRoleTheme } from '../../../hooks/useRoleTheme';
import { genreOptions, allContentSuggestions, getContentTagColor, themeSuggestions } from '../../../data/productManagerData';
import type { AgeRating } from '../../../data/productManagerTypes';
import { getGroupedSubgenres } from '../../../data/subgenreRegistry';
import { subGenreOptions, STRUCTURED_SUBGENRES, settingOptions, relationshipDynamics, relationshipTypes, tabooSuggestions, youngReaderAgeRanges, youngReaderMoodTags, youngReaderStoryTypes } from './constants';
import type { PM } from './useProductManager';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { RomanceTaggingSection } from './RomanceTaggingSection';
import { AfterTideSection } from './AfterTideSection';
interface CategoriesTagsSectionProps {
  pm: PM;
}
export const CategoriesTagsSection: React.FC<CategoriesTagsSectionProps> = ({
  pm
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    darkMode,
    productType,
    bookForm,
    setBookForm,
    merchForm,
    setMerchForm,
    eventForm,
    setEventForm,
    bookBoxForm,
    setBookBoxForm,
    creativeForm,
    setCreativeForm,
    serviceForm,
    setServiceForm,
    selectedSubGenres,
    setSelectedSubGenres,
    secondaryGenres,
    subGenreSearch,
    setSubGenreSearch,
    selectedSettings,
    setSelectedSettings,
    settingSearch,
    setSettingSearch,
    povType,
    setPovType,
    storyTense,
    setStoryTense,
    romancePace,
    setRomancePace,
    endingType,
    setEndingType,
    connectionType,
    setConnectionType,
    darknessLevel,
    setDarknessLevel,
    hasTaboo,
    setHasTaboo,
    selectedTaboo,
    setSelectedTaboo,
    tabooInput,
    setTabooInput,
    selectedRelTypes,
    setSelectedRelTypes,
    selectedDynamics,
    setSelectedDynamics,
    customDynamic,
    setCustomDynamic,
    showAddDynamic,
    setShowAddDynamic,
    specialtyInput,
    setSpecialtyInput,
    contentTagInput,
    setContentTagInput,
    themeInput,
    setThemeInput,
    handleAddContentTag,
    handleRemoveContentTag,
    getAllContentTags,
    handleAddTheme,
    handleRemoveRemoveTheme,
    handleAddSpecialty,
    handleRemoveSpecialty,
    handleAddSpecialtyService,
    handleRemoveSpecialtyService,
    handleAddCustomDynamic
  } = pm;
  const isRomance = bookForm.genre === 'Romance' || secondaryGenres.includes('Romance');
  // Get structured subgenres for the selected primary genre
  const currentRegistry = bookForm.genre ? STRUCTURED_SUBGENRES[bookForm.genre] : null;
  return <div className={`p-6 rounded-xl shadow-sm ${dk.card}`} data-id="element-20122" data-mp="27pzkxbk7rf3o">
      <h2 className={`text-lg font-bold mb-4 flex items-center ${dk.textHeading}`} data-id="element-20123" data-mp="118kqen1av2ks">
        <TagIcon className="w-5 h-5 mr-2 text-purple-500" data-id="element-20124" data-mp="w7x0y5spytso" />
        Categories & Tags
      </h2>
      <div className="space-y-4" data-id="element-20125" data-mp="37x4oo2utgtmc">
        {(productType === 'bookbox' || productType === 'merch') && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-20126" data-mp="3cvy2jwe0mmn1">
            <div data-id="element-20127" data-mp="3gw86l3i8z2z9">
              <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20128" data-mp="3gvoyeeiefsyf">
                Genre
              </label>
              <select value={productType === 'merch' ? merchForm.genre : bookBoxForm.genre || ''} onChange={e => {
            const val = e.target.value;
            if (productType === 'merch') setMerchForm({
              ...merchForm,
              genre: val
            });else setBookBoxForm({
              ...bookBoxForm,
              genre: val
            });
          }} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-20129" data-mp="3qmjl1jzk4uke">
                <option value="" data-id="element-20130" data-mp="1ax2ojolhdfee">Select Genre</option>
                {genreOptions.map(g => <option key={g} value={g} data-id="element-20131" data-mp="1owu9yaebty2f">
                    {g}
                  </option>)}
              </select>
            </div>
          </div>}

        {productType === 'book' && <div className="space-y-8" data-id="element-20132" data-mp="w6vcprpzaumo">
            {/* 4A. Universal Tags */}
            <div data-id="element-20133" data-mp="r8txxizl5hh0">
              <h3 className={`text-md font-bold mb-4 ${dk.textPrimary}`} data-id="element-20134" data-mp="r9bs8a2otlma">
                Universal Tags
              </h3>

              <div className="mb-6" data-id="element-20135" data-mp="15z16n6zsjm4i">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20136" data-mp="116q5y1x87waw">
                  Themes (Max 3)
                </label>
                <SearchWithAutocomplete value={themeInput} onChange={setThemeInput} suggestions={themeSuggestions} placeholder="Search themes..." onSelect={item => handleAddTheme(item.label)} onSearch={val => handleAddTheme(val)} data-id="element-20137" data-mp="df2kw0mnbfgj" />
                <div className="flex flex-wrap gap-2 mt-2" data-id="element-20138" data-mp="2b5r865dtv2yb">
                  {bookForm.themes?.map((theme, idx) => <span key={idx} className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium ${darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-900'}`} data-id="element-20139" data-mp="2f6um2mcbeoww">
                      {theme}
                      <button onClick={() => handleRemoveRemoveTheme(idx)} className="ml-1.5 hover:opacity-75" data-id="element-20140" data-mp="2tupgc75z11c3">
                        <XIcon className="w-3 h-3" data-id="element-20141" data-mp="1if99dgk4znqp" />
                      </button>
                    </span>)}
                </div>
              </div>

              <div data-id="element-20142" data-mp="3p4ufyt3abn6a">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20143" data-mp="3p39lt4kvu68y">
                  Content Warnings
                </label>
                <SearchWithAutocomplete value={contentTagInput} onChange={setContentTagInput} suggestions={allContentSuggestions.filter(s => s.type === 'Warning')} placeholder="Search warnings..." onSelect={item => {
              handleAddContentTag(item.label, 'Warning');
              setContentTagInput('');
            }} onSearch={val => {
              handleAddContentTag(val, 'Warning');
              setContentTagInput('');
            }} data-id="element-20144" data-mp="3fdzv9t5osvmy" />
                <div className="flex flex-wrap gap-2 mt-3" data-id="element-20145" data-mp="um6aytrfs2rv">
                  {getAllContentTags().filter(t => t.category === 'Warning').map((tag, idx) => <span key={idx} className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium ${getContentTagColor(tag.category, darkMode)}`} data-id="element-20146" data-mp="28ihfkwatobk4" data-mpi={idx}>
                        {tag.label}
                        <button onClick={() => handleRemoveContentTag(tag.label, tag.category)} className="ml-1.5 hover:opacity-75 focus:outline-none" data-id="element-20147" data-mp="2maxf3n4ilrga">
                          <XIcon className="w-3 h-3" data-id="element-20148" data-mp="1ykkwnibn8cbg" />
                        </button>
                      </span>)}
                </div>
              </div>
            </div>

            {/* 4B. Genre-Based Tags */}
            {bookForm.genre && <div className={`pt-6 border-t ${dk.border}`} data-id="element-20149" data-mp="3ezl15kx38kuw">
                <h3 className={`text-md font-bold mb-4 ${dk.textPrimary}`} data-id="element-20150" data-mp="3f27anq3jhafr">
                  {bookForm.genre} Tags
                </h3>

                {/* Sub-Genres */}
                <div className="mb-6" data-id="element-20151" data-mp="2lqkanzl9frd0">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20152" data-mp="2zfvv2xzy8r3q">
                    Sub-Genres
                  </label>
                  <SearchWithAutocomplete value={subGenreSearch} onChange={setSubGenreSearch} suggestions={getGroupedSubgenres(bookForm.genre).flatMap(group => group.entries.filter(entry => !selectedSubGenres.includes(entry.displayLabel)).map(entry => ({
              id: `subgenre-${entry.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
              label: entry.displayLabel,
              group: group.label,
              sublabel: entry.secondaryGenres?.length ? `Also in ${entry.secondaryGenres.join(', ')}` : undefined
            })))} placeholder={`Search ${bookForm.genre} sub-genres (e.g. ${(subGenreOptions[bookForm.genre] || []).slice(0, 2).join(', ')})...`} onSelect={item => {
              setSelectedSubGenres(prev => prev.includes(item.label) ? prev : [...prev, item.label]);
              setSubGenreSearch('');
            }} data-id="element-20153" data-mp="39ebml22w22dr" />
                  {selectedSubGenres.length > 0 && <div className="flex flex-wrap gap-2 mt-2" data-id="element-20154" data-mp="2biy2zj7rou9s">
                      {selectedSubGenres.map(sub => <span key={sub} className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium ${darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-900'}`} data-id="element-20155" data-mp="23bkeap5q1a85">
                          {sub}
                          <button onClick={() => setSelectedSubGenres(prev => prev.filter(g => g !== sub))} className="ml-1.5 hover:opacity-75" data-id="element-20156" data-mp="1ye0wjf9tefbg">
                            <XIcon className="w-3 h-3" data-id="element-20157" data-mp="2i0ymvv1h3nmh" />
                          </button>
                        </span>)}
                    </div>}
                </div>

                {/* Settings & Crossovers */}
                <div className="mb-6" data-id="element-20158" data-mp="16necsb43ixy3">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20159" data-mp="y4su7fjztzcg">
                    Settings & Crossovers
                  </label>
                  <SearchWithAutocomplete value={settingSearch} onChange={setSettingSearch} suggestions={settingOptions.filter(s => !selectedSettings.includes(s)).map(s => ({
              id: s.toLowerCase().replace(/\s+/g, '-'),
              label: s,
              type: 'Setting'
            }))} placeholder="Search settings (e.g. Small Town, Regency, Urban)..." onSelect={item => {
              setSelectedSettings(prev => prev.includes(item.label) ? prev : [...prev, item.label]);
              setSettingSearch('');
            }} data-id="element-20160" data-mp="182yvbuxz71g9" />
                  {selectedSettings.length > 0 && <div className="flex flex-wrap gap-2 mt-2" data-id="element-20161" data-mp="1wp9sd3mm3xsp">
                      {selectedSettings.map(s => <span key={s} className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium ${darkMode ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-900'}`} data-id="element-20162" data-mp="21i30m1haho9j">
                          {s}
                          <button onClick={() => setSelectedSettings(prev => prev.filter(x => x !== s))} className="ml-1.5 hover:opacity-75" data-id="element-20163" data-mp="1dtbbjiyzxm0z">
                            <XIcon className="w-3 h-3" data-id="element-20164" data-mp="2y6976txruqqg" />
                          </button>
                        </span>)}
                    </div>}
                </div>

                {/* Genre-specific tropes */}
                <div data-id="element-20165" data-mp="33e8odz6feb2x">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20166" data-mp="2ykxqwoni76no">
                    {bookForm.genre} Tropes
                  </label>
                  <SearchWithAutocomplete value={contentTagInput} onChange={setContentTagInput} suggestions={allContentSuggestions.filter(s => s.type === 'Trope')} placeholder="Search tropes..." onSelect={item => {
              handleAddContentTag(item.label, 'Trope');
              setContentTagInput('');
            }} onSearch={val => {
              handleAddContentTag(val, 'Trope');
              setContentTagInput('');
            }} data-id="element-20167" data-mp="2pk805zd3o6gt" />
                  <div className="flex flex-wrap gap-2 mt-3" data-id="element-20168" data-mp="guqjs8u16pdv">
                    {getAllContentTags().filter(t => t.category === 'Trope').map((tag, idx) => <span key={idx} className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium ${getContentTagColor(tag.category, darkMode)}`} data-id="element-20169" data-mp="bx6xqmr3d8bd" data-mpi={idx}>
                          {tag.label}
                          <button onClick={() => handleRemoveContentTag(tag.label, tag.category)} className="ml-1.5 hover:opacity-75 focus:outline-none" data-id="element-20170" data-mp="3kda8509l9b3e">
                            <XIcon className="w-3 h-3" data-id="element-20171" data-mp="3gbow1h4vp0u9" />
                          </button>
                        </span>)}
                  </div>
                </div>
              </div>}

            {/* 4C. Romance Module */}
            {isRomance && <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700" data-id="element-20172" data-mp="2rt527mx1j459">
                <RomanceTaggingSection pm={pm} data-id="element-20173" data-mp="35ogvrqzgmokl" />
              </div>}

            {/* 4D. Young Readers Module */}
            <div className={`pt-6 border-t ${dk.border}`} data-id="element-20174" data-mp="3fohgnhcz9cqw">
              <h3 className={`text-md font-bold mb-4 ${dk.textPrimary}`} data-id="element-20175" data-mp="3fmwnnw6ba1v5">
                Young Readers
              </h3>

              <div className="mb-6" data-id="element-20176" data-mp="3antp8uku6tw5">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20177" data-mp="3peu0f7cyx1vz">
                  Recommended Age Range
                </label>
                <select value={pm.recommendedAgeRange} onChange={e => pm.setRecommendedAgeRange(e.target.value as any)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-20178" data-mp="29bfmof8z6af">
                  {youngReaderAgeRanges.map(range => <option key={range.value} value={range.value} data-id="element-20179" data-mp="2vzq7x9bgvpqg">
                      {range.label}
                    </option>)}
                </select>
              </div>

              {pm.recommendedAgeRange !== 'adult-only' && <>
                  <div className="mb-6" data-id="element-20180" data-mp="39spn1a7c7hxh">
                    <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20181" data-mp="3esbybe3luo3n">
                      Young Reader Mood Tags
                    </label>
                    <div className="flex flex-wrap gap-2" data-id="element-20182" data-mp="3nsqlkj04qd9s">
                      {youngReaderMoodTags.map(tag => {
                  const isSelected = pm.selectedYRMoodTags.includes(tag);
                  return <button key={tag} onClick={() => pm.handleToggleYRMoodTag(tag)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${isSelected ? 'bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-900/50 dark:text-indigo-300 dark:border-indigo-700' : `${dk.surfaceMuted} ${dk.textSecondary} ${dk.border} hover:border-indigo-300`}`} data-id="element-20183" data-mp="1tg0vkccvto7o">
                            {tag}
                          </button>;
                })}
                    </div>
                  </div>

                  <div className="mb-6" data-id="element-20184" data-mp="1vte4b05bi2gy">
                    <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20185" data-mp="1vsbb2lkivnxv">
                      Story Types
                    </label>
                    <div className="flex flex-wrap gap-2" data-id="element-20186" data-mp="kf8vntu5t4fa">
                      {youngReaderStoryTypes.map(type => {
                  const isSelected = pm.selectedYRStoryTypes.includes(type);
                  return <button key={type} onClick={() => pm.handleToggleYRStoryType(type)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${isSelected ? 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/50 dark:text-emerald-300 dark:border-emerald-700' : `${dk.surfaceMuted} ${dk.textSecondary} ${dk.border} hover:border-emerald-300`}`} data-id="element-20187" data-mp="2jjqu4dn4063q">
                            {type}
                          </button>;
                })}
                    </div>
                  </div>
                </>}

              <div className={`p-4 rounded-lg border ${pm.isYoungReaderEligible.eligible ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'}`} data-id="element-20188" data-mp="3h6pt4v19xq4s">
                <h4 className={`text-sm font-bold mb-2 ${pm.isYoungReaderEligible.eligible ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`} data-id="element-20189" data-mp="15jnqpnt16dbp">
                  Young Readers Eligibility:{' '}
                  {pm.isYoungReaderEligible.eligible ? 'Eligible' : 'Not Eligible'}
                </h4>
                {!pm.isYoungReaderEligible.eligible && <ul className="list-disc pl-5 text-xs text-red-700 dark:text-red-400 space-y-1" data-id="element-20190" data-mp="mmcq9zvnxduo">
                    {pm.isYoungReaderEligible.reasons.map((reason, idx) => <li key={idx} data-id="element-20191" data-mp="vkeyazdtd6rg" data-mpi={idx}>{reason}</li>)}
                  </ul>}
                {pm.isYoungReaderEligible.eligible && <p className="text-xs text-green-700 dark:text-green-400" data-id="element-20192" data-mp="270vit7486dl6">
                    This book meets the criteria for the Young Readers platform.
                  </p>}
              </div>
            </div>
          </div>}

        {productType !== 'book' && <div data-id="element-20193" data-mp="26pf7eiwyajuu">
            <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20194" data-mp="26pyjhizx6e0y">
              {productType === 'creative' || productType === 'service' ? 'Specialties' : 'Tags, Tropes & Warnings'}
            </label>
            {productType === 'creative' || productType === 'service' ? <>
                <div className="flex gap-2" data-id="element-20195" data-mp="2pfx04nhs9fg7">
                  <input type="text" value={specialtyInput} onChange={e => setSpecialtyInput(e.target.value)} className={`flex-1 p-2 rounded-md border ${dk.inputOnCard}`} placeholder="Add specialty..." data-id="element-20196" data-mp="2pevfbraytela" />
                  <button onClick={productType === 'creative' ? handleAddSpecialty : handleAddSpecialtyService} className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700" data-id="element-20197" data-mp="2ugufytfblxej">
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2" data-id="element-20198" data-mp="393t34c75a5ng">
                  {(productType === 'creative' ? creativeForm.specialties : serviceForm.specialties)?.map((spec, idx) => <span key={idx} className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium ${darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-900'}`} data-id="element-20199" data-mp="2lcx7t53hcsbg">
                      {spec}
                      <button onClick={() => productType === 'creative' ? handleRemoveSpecialty(idx) : handleRemoveSpecialtyService(idx)} className="ml-1.5 hover:opacity-75" data-id="element-20200" data-mp="2gbzp8rek5w9f">
                        <XIcon className="w-3 h-3" data-id="element-20201" data-mp="d24pywa49qb7" />
                      </button>
                    </span>)}
                </div>
              </> : <>
                <SearchWithAutocomplete value={contentTagInput} onChange={setContentTagInput} suggestions={allContentSuggestions} placeholder="Search tropes, tags, warnings..." onSelect={item => {
            handleAddContentTag(item.label, item.type || 'Tag');
            setContentTagInput('');
          }} onSearch={val => {
            handleAddContentTag(val, 'Tag');
            setContentTagInput('');
          }} data-id="element-20202" data-mp="10u3i0rxsc0b1" />
                <div className="flex flex-wrap gap-2 mt-3" data-id="element-20203" data-mp="36halusab6f83">
                  {getAllContentTags().map((tag, idx) => <span key={idx} className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium ${getContentTagColor(tag.category, darkMode)}`} data-id="element-20204" data-mp="3bfuaj0gae545" data-mpi={idx}>
                      {tag.label}
                      <button onClick={() => handleRemoveContentTag(tag.label, tag.category)} className="ml-1.5 hover:opacity-75 focus:outline-none" data-id="element-20205" data-mp="3kb202dyzina5">
                        <XIcon className="w-3 h-3" data-id="element-20206" data-mp="2zsqhkbq30ok" />
                      </button>
                    </span>)}
                </div>
              </>}
          </div>}

        {productType === 'bookbox' && <div data-id="element-20207" data-mp="3v8spoypoo535">
            <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-20208" data-mp="3v89f632d4iz5">
              Spice Level
            </label>
            <div className="flex gap-2" data-id="element-20209" data-mp="3qcails6vrunn">
              {[1, 2, 3, 4, 5].map(level => {
            const currentSpice = bookBoxForm.spiceLevel;
            const isSelected = currentSpice === level;
            const isBelow = currentSpice ? level <= currentSpice : false;
            return <button key={level} onClick={() => {
              // Toggle off if clicking the same level
              if (isSelected) {
                setBookBoxForm({
                  ...bookBoxForm,
                  spiceLevel: undefined
                });
              } else {
                setBookBoxForm({
                  ...bookBoxForm,
                  spiceLevel: level
                });
              }
            }} className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${isSelected ? 'bg-red-500 text-white border-red-500 scale-110 shadow-md' : isBelow ? 'bg-red-100 border-red-300 dark:bg-red-900/40 dark:border-red-700' : `${dk.surfaceMuted} ${dk.border} hover:border-red-400`}`} data-id="element-20210" data-mp="37jq6vnjbc672">
                    <span className="text-lg" data-id="element-20211" data-mp="1n2gxlw33kwli">🌶️</span>
                  </button>;
          })}
            </div>
            {(() => {
          const currentSpice = bookBoxForm.spiceLevel;
          const spiceDescriptions: Record<number, {
            label: string;
            description: string;
            color: string;
            darkColor: string;
          }> = {
            1: {
              label: 'Mild',
              description: 'Closed door or fade-to-black — romance is implied, not shown',
              color: 'bg-sky-100 text-sky-700 border-sky-200',
              darkColor: 'bg-sky-500/20 text-sky-300 border-sky-700'
            },
            2: {
              label: 'Warm',
              description: 'Light steam — some sensual scenes but not explicit',
              color: 'bg-amber-100 text-amber-700 border-amber-200',
              darkColor: 'bg-amber-500/20 text-amber-300 border-amber-700'
            },
            3: {
              label: 'Spicy',
              description: 'Moderate heat — explicit scenes that are part of the story',
              color: 'bg-orange-100 text-orange-700 border-orange-200',
              darkColor: 'bg-orange-500/20 text-orange-300 border-orange-700'
            },
            4: {
              label: 'Extra Hot',
              description: 'High heat — frequent, detailed intimate scenes',
              color: 'bg-rose-100 text-rose-700 border-rose-200',
              darkColor: 'bg-rose-500/20 text-rose-300 border-rose-700'
            },
            5: {
              label: 'Inferno',
              description: 'Maximum heat — erotica-level content throughout',
              color: 'bg-red-100 text-red-700 border-red-200',
              darkColor: 'bg-red-500/20 text-red-300 border-red-700'
            }
          };
          if (!currentSpice || !spiceDescriptions[currentSpice]) {
            return <p className={`text-xs mt-2 ${dk.textMuted}`} data-id="element-20212" data-mp="iu0ov9njxhuj">
                    Click to set spice level (click again to remove)
                  </p>;
          }
          const desc = spiceDescriptions[currentSpice];
          return <div className={`mt-3 px-3 py-2.5 rounded-lg border text-sm ${darkMode ? desc.darkColor : desc.color}`} data-id="element-20213" data-mp="1y6ttw1rpgbg2">
                  <div className="flex items-center gap-2 font-semibold text-xs" data-id="element-20214" data-mp="2fbdpaggwys1z">
                    <span data-id="element-20215" data-mp="2tzrq6lurs6fw">🌶️</span>
                    <span data-id="element-20216" data-mp="2u0tcjmh8kfcx">
                      Level {currentSpice} — {desc.label}
                    </span>
                  </div>
                  <p className="text-xs mt-1 opacity-80" data-id="element-20217" data-mp="2p6yl1wve2w3a">{desc.description}</p>
                </div>;
        })()}
          </div>}

        {productType === 'bookbox' && <div data-id="element-20218" data-mp="2y13fsrdn8ro3">
            <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-20219" data-mp="2y3pncr0jb76b">
              Darkness Level
            </label>
            <div className="flex gap-2" data-id="element-20220" data-mp="380ubgwdxxcpr">
              {[1, 2, 3, 4, 5].map(level => {
            const isSelected = darknessLevel === level;
            const isBelow = darknessLevel ? level <= darknessLevel : false;
            return <button key={level} onClick={() => setDarknessLevel(darknessLevel === level ? 0 : level)} className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${isSelected ? 'bg-slate-700 text-white border-slate-700 scale-110 shadow-md' : isBelow ? 'bg-slate-200 border-slate-400 dark:bg-slate-700/60 dark:border-slate-600' : `${dk.surfaceMuted} ${dk.border} hover:border-slate-400`}`} data-id="element-20221" data-mp="3p93r8u3a3931">
                    <span className="text-lg" data-id="element-20222" data-mp="3alijunzdenpb">🌑</span>
                  </button>;
          })}
            </div>
            {(() => {
          const darknessDescriptions: Record<number, {
            label: string;
            description: string;
            color: string;
            darkColor: string;
          }> = {
            1: {
              label: 'Hint of Shadow',
              description: 'Light tension — mild conflict, no graphic content',
              color: 'bg-slate-100 text-slate-600 border-slate-200',
              darkColor: 'bg-slate-700/40 text-slate-300 border-slate-600'
            },
            2: {
              label: 'Into the Grey',
              description: 'Moderate darkness — morally grey characters, some intensity',
              color: 'bg-slate-200 text-slate-700 border-slate-300',
              darkColor: 'bg-slate-600/50 text-slate-200 border-slate-500'
            },
            3: {
              label: 'Embrace the Abyss',
              description: 'Dark themes — violence, trauma, or heavy emotional content',
              color: 'bg-slate-500 text-white border-slate-500',
              darkColor: 'bg-slate-500/60 text-slate-100 border-slate-400'
            },
            4: {
              label: 'Pitch Black',
              description: 'Very dark — graphic content, disturbing themes, anti-heroes',
              color: 'bg-slate-700 text-white border-slate-700',
              darkColor: 'bg-gray-700 text-gray-100 border-gray-600'
            },
            5: {
              label: 'The Void',
              description: 'Extreme darkness — no limits, reader discretion strongly advised',
              color: 'bg-gray-900 text-white border-gray-900',
              darkColor: 'bg-black/70 text-white border-gray-700'
            }
          };
          if (darknessLevel === 0 || !darknessDescriptions[darknessLevel]) {
            return <p className={`text-xs mt-2 ${dk.textMuted}`} data-id="element-20223" data-mp="3r17dos8ajwvw">
                    Click to set darkness level (click again to remove)
                  </p>;
          }
          const desc = darknessDescriptions[darknessLevel];
          return <div className={`mt-3 px-3 py-2.5 rounded-lg border text-sm ${darkMode ? desc.darkColor : desc.color}`} data-id="element-20224" data-mp="2i3r0uh4uomzw">
                  <div className="flex items-center gap-2 font-semibold text-xs" data-id="element-20225" data-mp="243gadf7gdaf9">
                    <span data-id="element-20226" data-mp="291zynm9iqyas">🌑</span>
                    <span data-id="element-20227" data-mp="2955myzabpw5g">
                      Level {darknessLevel} — {desc.label}
                    </span>
                  </div>
                  <p className="text-xs mt-1 opacity-80" data-id="element-20228" data-mp="1z4my1q3wjpgo">{desc.description}</p>
                </div>;
        })()}
          </div>}
      </div>
    </div>;
};